// Public page that consumes a verification token from the email link.
//
// Flow:
//   1. User clicks the link in their inbox → lands on /verify-email?token=...
//   2. We POST the token to /auth/email/verify
//   3. On success: show "✓ Verified — sign in to continue" with a CTA back to /login
//   4. On failure: show "this link is invalid or expired" + a way to request a new one
//
// Lives outside the authed shell — works whether the user is signed in or not
// (the token alone proves email ownership).

import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Loading } from "@/components/ui/Loading";
import { useDict } from "@/i18n/LocaleProvider";
import { ApiError, api } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";

type State =
  | { kind: "verifying" }
  | { kind: "ok"; email: string }
  | { kind: "error"; message: string };

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const accessToken = useAuthStore((s) => s.accessToken);
  const refreshUser = useAuthStore((s) => s.refreshUser);
  const [state, setState] = useState<State>({ kind: "verifying" });
  const t = useDict().app.auth;

  useEffect(() => {
    if (!token) {
      setState({
        kind: "error",
        message: t.noTokenMsg,
      });
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const r = await api<{ message: string; email: string }>(
          "/auth/email/verify",
          { method: "POST", body: { token } },
        );
        if (cancelled) return;
        // If the user is logged in (the common case — they registered
        // in this same browser), refresh the cached user so
        // user.email_verified flips True locally and RequireAuth
        // stops redirecting them to /verify-email-required.
        if (accessToken) {
          try {
            await refreshUser();
          } catch {
            // Network blip — user can re-login to refresh state.
          }
        }
        setState({ kind: "ok", email: r.email });
      } catch (e) {
        if (cancelled) return;
        // The backend collapses unknown / expired / used into the same
        // `code: invalid_or_expired_token` so we don't leak which.
        const msg =
          e instanceof ApiError && typeof e.detail === "object" && e.detail !== null
            ? (e.detail as { message?: string }).message ??
              t.invalidMsg
            : t.invalidMsg;
        setState({ kind: "error", message: msg });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-5">
      <div className="w-full max-w-[420px]">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="TrueHacking" className="h-20 w-auto mb-4 mx-auto" />
          <div className="font-display text-[32px] font-bold text-accent tracking-[0.14em]">
            TRUEHACKING
          </div>
          <div className="font-mono text-[10px] text-dim tracking-[0.1em] mt-1 uppercase">
            {t.verifySubtitle}
          </div>
        </div>

        <Card className="!p-5 sm:!p-7">
          {state.kind === "verifying" && (
            <Loading text={t.verifying} />
          )}

          {state.kind === "ok" && (
            <>
              <div className="font-display text-[18px] font-semibold text-content mb-2">
                {t.verifiedTitle}
              </div>
              <div className="font-mono text-[11px] text-muted leading-[1.6] mb-5">
                <span className="text-accent">{state.email}</span>{" "}
                {t.verifiedConfirmed}
                {accessToken ? t.verifiedSignedIn : t.verifiedSignIn}
              </div>
              <Button
                variant="primary"
                full
                onClick={() => navigate(accessToken ? "/app" : "/login", { replace: true })}
              >
                {accessToken ? t.goDashboard : t.continueSignIn}
              </Button>
            </>
          )}

          {state.kind === "error" && (
            <>
              <div className="font-display text-[18px] font-semibold text-danger mb-2">
                {t.linkInvalidTitle}
              </div>
              <div className="font-mono text-[11px] text-muted leading-[1.6] mb-5">
                {state.message}
              </div>
              <Link to="/verify-email-required" className="no-underline">
                <Button variant="primary" full>
                  {t.requestNewLink}
                </Button>
              </Link>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
